import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DialogProps } from "@validereinc/common-components";
import { Button, Dialog, useToast } from "@validereinc/common-components";
import { FormSchemaAdapter, Resources } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";

const STATUS_MAPPING = {
  archived: {
    present: "archive",
    past: "archived",
    warning:
      "Users will no longer be able to create submissions for this form.",
  },
  active: {
    present: "activate",
    past: "activated",
    warning: "Users will now be able to create submissions for this form.",
  },
  draft: {
    present: "draft",
    past: "drafted",
    warning:
      "Users will no longer be able to create submissions for this form.",
  },
};

export const UpdateFormTemplateStatusDialog = ({
  isOpen,
  onClose,
  formSchemaId,
  status,
}: Pick<DialogProps, "isOpen" | "onClose"> & {
  formSchemaId: string;
  status?: "archived" | "active" | "draft";
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    present,
    warning,
  }: { past?: string; present?: string; warning?: string } = status
    ? STATUS_MAPPING[status]
    : {};

  const { mutate, isLoading: isMutating } = useMutation({
    mutationFn: (params: Parameters<typeof FormSchemaAdapter.updateOne>[0]) =>
      FormSchemaAdapter.updateOne(params),
    onSuccess: ({ data: { status: newStatus } }) => {
      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_SCHEMA],
      });

      const { past }: { past?: string; present?: string; warning?: string } =
        newStatus ? STATUS_MAPPING[newStatus] : {};

      toast.push({
        intent: "success",
        description: `Successfully ${past} template.`,
      });
    },
    onError: () => {
      const { past }: { past?: string; present?: string; warning?: string } =
        status ? STATUS_MAPPING[status] : {};

      toast.push({
        intent: "error",
        description: `Couldn't ${past} template.`,
      });
    },
  });

  const onSubmit = () => {
    mutate({
      id: formSchemaId,
      data: {
        status,
      },
    });

    onClose();
  };

  return (
    <Dialog
      title={`${toTitleCaseString(present, { intent: "ui" })} Template?`}
      isOpen={isOpen && !!status}
      onClose={onClose}
      actionRow={[
        <Button
          key="archive"
          variant="error-outline"
          isLoading={isMutating}
          onClick={onSubmit}
        >
          {toTitleCaseString(present, { intent: "ui" })} Template
        </Button>,
      ]}
    >
      Are you sure you want to {present} this template? {warning}
    </Dialog>
  );
};
